/**
 * Upload utilities — client-side compression + one-file-per-request uploads.
 *
 * Why this design (solves Cloudflare 520 timeouts in production):
 *  - Client compresses every image to ~300–800 KB JPEG BEFORE sending.
 *    Backend receives tiny payloads and processes each in <500ms.
 *  - Each file goes in its own POST. Every request finishes in 1–2 s,
 *    far below Cloudflare's 100 s proxy timeout, so 520 errors become impossible
 *    in normal network conditions.
 *  - No auto-retry (avoids duplicate uploads if the server saved but response
 *    was lost). Instead, callers get `failedFiles` and can offer a "retry"
 *    button so the user re-attempts ONLY the failed items.
 */
const SIZE_SKIP_COMPRESSION = 300 * 1024;   // Files < 300 KB skip canvas re-encode
const DEFAULT_MAX_LONG_SIDE = 1600;
const DEFAULT_JPEG_QUALITY = 0.82;

/**
 * Attempt to decode an image with the browser and re-encode as a small JPEG.
 *
 * Preserves PNG transparency: if the source is a PNG (`image/png`) OR the file
 * extension indicates PNG, the compressed output is also PNG (keeps alpha).
 * For everything else, output is JPEG (much smaller for photos).
 *
 * Returns { blob, name, compressed } where:
 *   - blob: the file to send (compressed JPEG/PNG when possible, else original)
 *   - name: filename with correct extension for the output format
 *   - compressed: boolean flag
 *
 * If the browser cannot decode the file (typical for HEIC on Chrome/Firefox),
 * we fall back to the original bytes; the backend handles decoding via
 * pillow-heif, and each request stays small enough to stay under timeouts.
 */
export async function compressImage(file, {
  maxLongSide = DEFAULT_MAX_LONG_SIDE,
  quality = DEFAULT_JPEG_QUALITY,
} = {}) {
  if (!file || file.size < SIZE_SKIP_COMPRESSION) {
    // Small files (< 300 KB) go straight through — preserves original format
    // and any transparency without any risk of quality loss.
    return { blob: file, name: file?.name || 'file', compressed: false };
  }

  // Detect PNG (either by MIME type or filename extension) so we can preserve alpha.
  const isPng = (file.type === 'image/png') || /\.png$/i.test(file.name || '');
  const outputMime = isPng ? 'image/png' : 'image/jpeg';
  const outputExt = isPng ? 'png' : 'jpg';

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('browser decode failed'));
      el.src = objectUrl;
    });

    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error('no dimensions');

    const long = Math.max(w, h);
    if (long > maxLongSide) {
      const r = maxLongSide / long;
      w = Math.round(w * r);
      h = Math.round(h * r);
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!isPng) {
      // White background flattens any residual transparency for JPEG output
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
    }
    // PNG path leaves the canvas fully transparent so drawing preserves alpha
    ctx.drawImage(img, 0, 0, w, h);

    // Only pass quality to toBlob for lossy formats
    const blob = await new Promise((resolve) => {
      if (isPng) {
        canvas.toBlob(resolve, outputMime);
      } else {
        canvas.toBlob(resolve, outputMime, quality);
      }
    });

    if (!blob) throw new Error('canvas toBlob returned null');

    // Only use compressed output if it's noticeably smaller than the original
    if (blob.size >= file.size * 0.95) {
      return { blob: file, name: file.name, compressed: false };
    }

    const newName = (file.name || 'file').replace(/\.[^.]+$/, '') + '.' + outputExt;
    return { blob, name: newName, compressed: true };
  } catch (_e) {
    // Common on HEIC in Chrome/Firefox — server will handle decoding.
    return { blob: file, name: file.name, compressed: false };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Detect Cloudflare / edge-proxy timeouts so we can show a friendlier message.
 */
function humanizeError(e) {
  const status = e?.response?.status;
  const raw =
    (typeof e?.response?.data === 'string' ? e.response.data : '') ||
    e?.response?.data?.detail ||
    e?.message ||
    'error de red';
  if (status === 520 || status === 522 || status === 524 || /cloudflare|origin.*could not parse|timeout/i.test(raw)) {
    return 'Timeout de red (Cloudflare). Toca "Reintentar fallidos" para volver a intentar sólo este archivo.';
  }
  return raw.slice(0, 240);
}

/**
 * Upload files ONE AT A TIME. Compress each in-browser before uploading.
 *
 * @param {FileList|File[]} files
 * @param {(blob: Blob|File, name: string, originalFile: File) => Promise<any>} uploadOne
 *        Async function that uploads a single file and returns the server's JSON response.
 * @param {(p: {
 *   completed: number, total: number, currentName: string|null,
 *   phase: 'compress'|'upload'|'done',
 *   inserted: number, errors: number, lastData: any
 * }) => void} onProgress
 *
 * @returns {Promise<{ inserted:number, errors: Array<{name:string, error:string}>,
 *                    failedFiles: File[], lastData: any }>}
 */
export async function uploadFiles(files, uploadOne, onProgress) {
  const arr = Array.from(files || []);
  const total = arr.length;
  const result = {
    inserted: 0,
    errors: [],
    failedFiles: [],
    lastData: null,
  };

  const emit = (extra) => {
    onProgress && onProgress({
      completed: 0,
      total,
      currentName: null,
      phase: 'compress',
      inserted: result.inserted,
      errors: result.errors.length,
      lastData: result.lastData,
      ...extra,
    });
  };

  emit({ completed: 0, phase: 'compress' });

  for (let i = 0; i < total; i++) {
    const original = arr[i];
    emit({ completed: i, currentName: original.name, phase: 'compress' });

    let prepared;
    try {
      prepared = await compressImage(original);
    } catch (_e) {
      prepared = { blob: original, name: original.name, compressed: false };
    }

    emit({ completed: i, currentName: original.name, phase: 'upload' });

    try {
      const data = await uploadOne(prepared.blob, prepared.name, original);

      if (data && typeof data === 'object') {
        result.inserted += Number(data.inserted || 0);
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          // Per-file errors reported by the server (validation, bad image, etc.)
          for (const err of data.errors) {
            result.errors.push({ name: err.name || original.name, error: err.error || 'error' });
          }
          result.failedFiles.push(original);
        }
        result.lastData = data;
      }
    } catch (e) {
      result.errors.push({ name: original.name, error: humanizeError(e) });
      result.failedFiles.push(original);
    }

    emit({ completed: i + 1, currentName: null, phase: 'upload' });
  }

  emit({ completed: total, currentName: null, phase: 'done' });
  return result;
}
